import { Pipe, PipeTransform } from '@angular/core';
import { ImpactDataFormateEnum } from '../reportConstant';

@Pipe({
  name: 'decimalFormatPipe',
})
export class DecimalFormatPipePipe implements PipeTransform {
  transform(
    impactValue: any,
    impactValueType: ImpactDataFormateEnum = ImpactDataFormateEnum.Default
  ): string {
    impactValue = impactValue?.toString();
    if (!impactValue || impactValue.trim() === '') {
      return impactValue;
    }

    const isPercentage = impactValue.includes('%');
    const numericPart = isPercentage ? impactValue.replace('%', '') : impactValue;

    const impact = parseFloat(numericPart.replace(/,/g, ''));
    if (isNaN(impact)) {
      return impactValue;
    }

    // String already contained "%" → format and re-append "%"
    if (isPercentage) {
      return this.formatImpactValue(impact, numericPart) + '%';
    }

    // Percentage type → same formatting, no "%" re-append
    if (impactValueType === ImpactDataFormateEnum.Percentage) {
      return this.formatImpactValue(impact, numericPart);
    }

    if (impactValueType === ImpactDataFormateEnum.Units) {
      return this.formatImpactValue(impact, numericPart);
    }

    return this.formatImpactValue(impact, numericPart);
  }

  private formatImpactValue(impact: number, originalString?: string): string {
    if (impact === 0) return '0';

    const abs = Math.abs(impact);

    // abs >= 100,000 → scientific  e.g. 1.23E+05
    if (abs >= 100000) {
      return this.formatScientific(impact);
    }

    // 1,000 <= abs < 100,000 → integer + thousand separators  e.g. 1,235
    if (abs >= 1000) {
      return this.roundToInteger(impact).toLocaleString('en-US');
    }

    // 100 <= abs < 1,000 → plain integer, no separators  e.g. 123
    if (abs >= 100) {
      return this.roundToInteger(impact).toString();
    }

    // 1 <= abs < 100 → integer stays integer; non-integer → 3 SF
    // e.g. 5 → "5",  1.23658 → "1.24",  12.345 → "12.3"
    if (abs >= 1) {
      if (impact === Math.floor(impact)) {
        return impact.toFixed(0);
      }
      return this.formatThreeSF(impact, originalString);
    }

    // 0.1 <= abs < 1 → 3 SF
    // e.g. 0.568764 → "0.569",  0.4899 → "0.490"
    if (abs >= 0.1) {
      return this.formatThreeSF(impact, originalString);
    }

    // 0.001 <= abs < 0.1 → exactly 3 decimal places
    // e.g. 0.0050 → "0.005",  0.0234 → "0.023"
    if (abs >= 0.001) {
      return this.formatThreeDecimals(impact, originalString);
    }

    // abs < 0.001 → scientific  e.g. 0.0005 → "5.00E-04"
    return this.formatScientific(impact);
  }

  /**
   * Rounds to 3 significant figures.
   *
   * magnitude = floor(log10(abs))
   * decPlaces = 2 - magnitude  ← this is the "3 SF" formula
   *
   * Why 2 - magnitude?
   *   A number in [1, 10)  has magnitude 0 → needs 2 dp → "1.24"  (1 + 2 = 3 SF)
   *   A number in [10,100) has magnitude 1 → needs 1 dp → "43.0"  (2 + 1 = 3 SF)
   *   A number in [0.1, 1) has magnitude -1 → needs 3 dp → "0.236" (0 + 3 = 3 SF)
   *
   * Uses originalString for decimal-exact rounding to avoid float drift.
   * e.g. "12.345" as float = 12.34499... → wrongly rounds to 12.3 without this.
   *
   * Boundary protection: if rounding pushes [0.1,1) to >=1 (e.g. 0.99999 → 1.000),
   * truncates to 2 dp instead (→ 0.99).
   */
  private formatThreeSF(value: number, originalString?: string): string {
    const abs = Math.abs(value);
    const magnitude = Math.floor(Math.log10(abs));
    const decPlaces = Math.max(0, 2 - magnitude); // 3 SF formula

    let rounded: number;
    const cleanStr = this.cleanNumericString(originalString);
    if (cleanStr !== undefined && cleanStr !== null && !isNaN(Number(cleanStr))) {
      rounded = this.roundToDecimalExact(cleanStr, decPlaces);
    } else {
      rounded = this.roundToDecimal(value, decPlaces);
    }

    const roundedAbs = Math.abs(rounded);

    // Boundary protection: 0.99999 rounded to 3 SF = 1.000 → truncate to 2 dp = 0.99
    if (roundedAbs >= 1.0 && abs < 1.0) {
      rounded = Math.trunc(value * 100) / 100;
    }

    const finalAbs = Math.abs(rounded);

    // Re-delegate if rounding pushed out of range (e.g. 99.995 → 100)
    if (finalAbs >= 100 || (rounded !== 0 && finalAbs < 0.1)) {
      return this.formatImpactValue(rounded);
    }

    return this.applyThreeSFFormat(rounded, decPlaces);
  }

  /**
   * Applies the correct format string based on decimal places needed for 3 SF.
   *
   * decPlaces 1 → [10, 100) : always 1 dp           43.0  -42.7
   * decPlaces 2 → [ 1,  10) : 1–2 dp, drop 2nd zero  1.24   5.60→5.6
   * decPlaces 3 → [0.1,  1) : 2–3 dp, drop 3rd zero  0.490  0.236
   */
  private applyThreeSFFormat(value: number, decPlaces: number): string {
    if (decPlaces === 1) {
      // Always exactly 1 dp  e.g. 43.0
      return value.toFixed(1);
    }

    if (decPlaces === 2) {
      // 1–2 dp: drop trailing zero in 2nd dp only
      // e.g. 12.40 → "12.4",  5.68 → "5.68",  1.24 → "1.24"
      return value.toFixed(2);
    }

    if (decPlaces === 3) {
      // 2–3 dp: enforce min 2 dp, drop 3rd dp only if zero
      // e.g. 0.490 → "0.490",  0.236 → "0.236",  0.500 → "0.500"
      return value.toFixed(3);
    }

    return value.toFixed(decPlaces).replace(/\.?0+$/, '');
  }

  /**
   * Renders exactly 3 decimal places for the [0.001, 0.1) band.
   * e.g. 0.0050 → "0.005",  0.0234 → "0.023".
   * Uses decimal-exact rounding from the original string when available
   * to avoid float drift.
   */
  private formatThreeDecimals(value: number, originalString?: string): string {
    let rounded: number;
    const cleanStr = this.cleanNumericString(originalString);
    if (cleanStr !== undefined && cleanStr !== null && !isNaN(Number(cleanStr))) {
      rounded = this.roundToDecimalExact(cleanStr, 3);
    } else {
      rounded = this.roundToDecimal(value, 3);
    }
    return rounded.toFixed(3);
  }

  /**
   * Strips "%" and thousand separators, then expands scientific notation
   * ("6.31E-03" → "0.00631") so the digit-based exact rounding can process
   * it — roundToDecimalExact splits on "." and would otherwise misread the
   * mantissa digits and ignore the exponent (e.g. "6.31E-03" → 0.063).
   * Returns undefined when the string still isn't a plain decimal, so
   * callers fall back to float rounding instead of corrupting the value.
   */
  private cleanNumericString(originalString?: string): string | undefined {
    let cleanStr = originalString?.replace('%', '').replace(/,/g, '');
    if (cleanStr === undefined || cleanStr === null) {
      return undefined;
    }
    if (/[eE]/.test(cleanStr)) {
      cleanStr = this.expandScientific(cleanStr);
    }
    return /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(cleanStr.trim()) ? cleanStr.trim() : undefined;
  }

  private expandScientific(numericString: string): string {
    const match = numericString.trim().match(/^([+-]?)(\d*)(?:\.(\d*))?[eE]([+-]?\d+)$/);
    if (!match || (!match[2] && !match[3])) {
      return numericString;
    }
    const sign = match[1] === '-' ? '-' : '';
    const intPart = match[2] || '';
    const fracPart = match[3] || '';
    const exponent = parseInt(match[4], 10);
    const digits = intPart + fracPart;
    const pointIndex = intPart.length + exponent;
    if (pointIndex <= 0) {
      return sign + '0.' + '0'.repeat(-pointIndex) + digits;
    }
    if (pointIndex >= digits.length) {
      return sign + digits + '0'.repeat(pointIndex - digits.length);
    }
    return sign + digits.slice(0, pointIndex) + '.' + digits.slice(pointIndex);
  }

  private roundToDecimalExact(numericString: string, decimalPlaces: number): number {
    const parts = numericString.split('.');
    const intPart = parts[0];
    const fracPart = (parts[1] ?? '').padEnd(decimalPlaces + 1, '0');

    const combined = intPart + fracPart.slice(0, decimalPlaces + 1);
    const shifted = parseInt(combined, 10);

    const lastDigit = Math.abs(shifted) % 10;
    const sign = shifted < 0 ? -1 : 1;
    const truncated = Math.trunc(shifted / 10);
    const rounded = lastDigit >= 5 ? truncated + sign : truncated;

    return rounded / Math.pow(10, decimalPlaces);
  }

  private roundToDecimal(value: number, decimalPlaces: number): number {
    const factor = Math.pow(10, decimalPlaces);
    return (Math.round(Math.abs(value) * factor) / factor) * Math.sign(value);
  }

  private roundToInteger(value: number): number {
    return Math.round(value);
  }

  private formatScientific(num: number): string {
    const exp = num.toExponential(2);
    const [mantissa, exponent] = exp.split('e');
    const sign = exponent.startsWith('-') ? '-' : '+';
    const expValue = exponent.replace(/^[-+]/, '').padStart(2, '0');
    return `${mantissa.toUpperCase()}E${sign}${expValue}`;
  }
}
